import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPlans, clearError } from "../../features/payment/paymentSlice";
import { GetStartedButton } from "../../components/common/GetStartedButton";

const COACH_ROLE = "coach";

// The coach plans on the signup step are these seat-based packages, whose
// names don't contain "coach" — include them explicitly (same as signup).
const EXTRA_PLAN_NAMES = ["staff package", "program package"];

// Strip interval words so monthly + yearly collapse into one card
function getBasePlanName(name) {
  return (name || "")
    .replace(/\b(monthly|yearly|annual|month|year)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

const formatPrice = (price) =>
  `$${Number(price).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

// Plan cards are navy in both themes (same as the home page pricing box),
// so the text colors inside them are fixed rather than theme tokens.
const CheckIcon = () => (
  <svg className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const PricingPage = () => {
  const dispatch = useDispatch();

  const { plans, loading, error } = useSelector((state) => state.payment);

  useEffect(() => {
    dispatch(fetchPlans(COACH_ROLE));
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Same coach plans that show on the coach signup step (yearly only)
  const coachPlans = useMemo(() => {
    const filtered = plans.filter((plan) => {
      const name = (plan.name || "").toLowerCase().trim();
      const idMatch = (plan.planId || plan._id || "").toLowerCase().includes(COACH_ROLE);
      const isExtra = EXTRA_PLAN_NAMES.includes(name);
      return name.includes(COACH_ROLE) || idMatch || isExtra;
    });

    // Group by base name and keep the yearly variant
    const groups = {};
    filtered.forEach((plan) => {
      const baseName = getBasePlanName(plan.name);
      if (!groups[baseName]) groups[baseName] = { baseName, monthly: null, yearly: null };
      const interval = (plan.interval || "month").toLowerCase();
      if (interval === "year" || interval === "yearly" || interval === "annual") {
        groups[baseName].yearly = plan;
      } else {
        groups[baseName].monthly = plan;
      }
    });

    return Object.values(groups).map((group) => {
      const active = group.yearly || group.monthly;
      const features = new Set();
      group.monthly?.features?.forEach((f) => features.add(f));
      group.yearly?.features?.forEach((f) => features.add(f));
      return {
        title: group.baseName || active?.name || "Coach",
        description: active?.description,
        features: [...features],
        price: active?.price,
      };
    });
  }, [plans]);

  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Header band: navy in both themes so the white navbar stays readable */}
      <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-14 sm:pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.7fr_1fr] gap-8 lg:gap-12 lg:items-end">
          <div>
            <p className="text-yellow-300 text-sm sm:text-base font-bold uppercase tracking-widest mb-4">
              Pricing
            </p>
            <h1 className="text-5xl sm:text-6xl xl:text-7xl font-anton leading-[1.1] tracking-tight text-white">
              FIND THE PLAN
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 text-transparent bg-clip-text">
                THAT FITS
              </span>
            </h1>
          </div>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            Whether you're an athlete just beginning your journey or a coach
            building a competitive roster — we have a plan that fits.
          </p>
        </div>
      </section>

      {/* Plans */}
      <section className="bg-jp-section border-b border-jp-line px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center text-jp-muted">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-jp-accent mb-4"></div>
              <p>Loading plans...</p>
            </div>
          ) : error ? (
            <div className="text-center text-red-600 dark:text-red-300">{error}</div>
          ) : coachPlans.length === 0 ? (
            <div className="text-center text-jp-muted">
              No plans available right now. Please check back later.
            </div>
          ) : (
            <div className="flex flex-col md:flex-row md:flex-wrap justify-center gap-6">
              {coachPlans.map((plan, index) => (
                <div
                  key={index}
                  className="flex flex-col w-full md:max-w-md md:flex-1 rounded-2xl p-6 sm:p-8 bg-jp-band text-white border border-jp-outline/70"
                >
                  <p className="text-slate-300 text-sm font-bold uppercase tracking-wider mb-2">
                    {plan.title}
                  </p>
                  <p className="mb-6">
                    <span className="text-5xl font-extrabold text-cyan-400 tabular-nums">
                      {formatPrice(plan.price)}
                    </span>
                    <span className="text-slate-300 text-sm"> / year</span>
                  </p>

                  {plan.description && (
                    <div
                      className="mb-6 text-slate-300 text-sm leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: plan.description }}
                    />
                  )}

                  {plan.features.length > 0 && (
                    <ul className="space-y-2.5 mb-8">
                      {plan.features.map((feature, i) => (
                        <li key={i} className="flex gap-2 text-slate-200 text-sm">
                          <CheckIcon />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-auto">
                    <GetStartedButton className="w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default PricingPage;
